import React, {
  forwardRef,
  type MouseEventHandler,
  type FC,
  type ComponentProps,
} from 'react';
import type { LabelAccessibilityT } from '@mentimeter/ragnar-styled';
import { styled } from '@mentimeter/ragnar-styled';
import { cn } from '@mentimeter/ragnar-tailwind-config';
import { styledTextRule, type TextT } from '../text';

export interface LinkT extends TextT, LabelAccessibilityT {
  href?: string | undefined;
  rel?: string | undefined;
  target?: string | undefined;
  underline?: boolean | undefined;
  truncate?: boolean;
  onClick?: MouseEventHandler<HTMLAnchorElement> | undefined;
}

export const FelaLink = styled('a', {
  displayName: 'Link',
})(
  styledTextRule,
  'color',
  'letterSpacing',
  'textAlign',
  'kosmosLineHeight',
  'kosmosSpacing',
  'kosmosFontSize',
  'maxWidth',
  'width',
  'display',
  'overflow',
  'flexbox',
  'position',
) as FC<LinkT>;

export const Link = forwardRef(
  (props: ComponentProps<typeof FelaLink>, ref) => {
    const { truncate, underline = true, className, ...rest } = props;

    const truncateClasses = [
      'text-ellipsis',
      'whitespace-nowrap',
      'overflow-hidden',
      'block',
      'max-w-full',
    ];

    const defaultClasses = [
      'r-link', // Reset selector
      underline ? 'underline' : 'no-underline',
      'text-text-primary',
      'font-body',
      'leading-normal',
      'break-words',
      'text-size-inherit',
      'rounded-default',
      'hover:opacity-60',
      'active:opacity-60',
      'cursor-pointer',
      'focus-visible:outline-4',
      'focus-visible:outline-solid',
      'focus-visible:outline-interactive-focused',
      'focus-visible:outline-offset-2',
    ];

    if (truncate) defaultClasses.push(...truncateClasses);

    return (
      <FelaLink {...rest} ref={ref} className={cn(defaultClasses, className)} />
    );
  },
);
